import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Handshake, CheckCircle2, XCircle, Mail, Phone, Building2, Copy, Loader2,
  Eye, RefreshCw, Trash2, Sparkles,
} from "lucide-react";

const STATUT_LABEL = {
  nouveau: "Nouveau",
  contacte: "Contacté",
  qualifie: "Qualifié",
  converti: "Converti",
  rejete: "Rejeté",
};
const STATUT_COLOR = {
  nouveau: "bg-blue-100 text-blue-800 hover:bg-blue-100",
  contacte: "bg-amber-100 text-amber-800 hover:bg-amber-100",
  qualifie: "bg-violet-100 text-violet-800 hover:bg-violet-100",
  converti: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100",
  rejete: "bg-red-100 text-red-700 hover:bg-red-100",
};

const FEAT_LABEL = {
  dossiers: "Dossiers",
  reclamations: "Réclamations",
  appels: "Appels",
  tickets: "Tickets",
  analytics: "Analytics",
  ged: "GED / OCR",
};

export default function Prospects() {
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [approveResult, setApproveResult] = useState(null); // {access_code, email}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { statut: filter };
      const [listRes, statsRes] = await Promise.all([
        api.get("/prospects", { params }),
        api.get("/prospects/stats"),
      ]);
      setProspects(listRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast.error("Erreur de chargement", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetails = async (id) => {
    try {
      const res = await api.get(`/prospects/${id}`);
      setSelected(res.data);
    } catch (e) {
      toast.error("Impossible de charger le prospect");
    }
  };

  const updateStatut = async (id, statut) => {
    try {
      await api.patch(`/prospects/${id}`, { statut });
      toast.success(`Statut → ${STATUT_LABEL[statut]}`);
      await load();
      if (selected?.id === id) {
        const res = await api.get(`/prospects/${id}`);
        setSelected(res.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const saveNotes = async (id, notes) => {
    try {
      await api.patch(`/prospects/${id}`, { notes_admin: notes });
      toast.success("Notes enregistrées");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const approve = async (id) => {
    try {
      const res = await api.post(`/prospects/${id}/approve`);
      setApproveResult({ access_code: res.data.access_code, email: res.data.email, entreprise: selected?.entreprise });
      setSelected(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approbation impossible");
    }
  };

  const reject = async (id) => {
    if (!window.confirm("Rejeter définitivement cette demande ?")) return;
    try {
      await api.post(`/prospects/${id}/reject`);
      toast.success("Prospect rejeté");
      setSelected(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ce prospect ? Cette action est irréversible.")) return;
    try {
      await api.delete(`/prospects/${id}`);
      toast.success("Prospect supprimé");
      setSelected(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(approveResult.access_code);
      toast.success("Code copié");
    } catch {
      toast.error("Copie impossible");
    }
  };

  const kpis = [
    { label: "Nouveau", value: stats?.nouveau ?? 0, tone: "bg-blue-100 text-blue-800" },
    { label: "Contactés", value: stats?.contacte ?? 0, tone: "bg-amber-100 text-amber-800" },
    { label: "Qualifiés", value: stats?.qualifie ?? 0, tone: "bg-violet-100 text-violet-800" },
    { label: "Convertis", value: stats?.converti ?? 0, tone: "bg-emerald-100 text-emerald-700" },
    { label: "Rejetés", value: stats?.rejete ?? 0, tone: "bg-red-100 text-red-700" },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Administration"
        title="Prospects — demandes d'inscription"
        description="Gérez les demandes d'accès entreprises reçues via le formulaire public /inscription."
        testid="prospects-header"
        actions={
          <Button
            variant="outline"
            className="rounded-full"
            onClick={load}
            disabled={loading}
            data-testid="prospects-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Rafraîchir
          </Button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="prospects-kpis">
        {kpis.map((k) => (
          <Card key={k.label} className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">{k.label}</div>
                <div className="font-display text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">{k.value}</div>
              </div>
              <div className={`w-9 h-9 rounded-lg grid place-items-center ${k.tone}`}>
                <Handshake className="w-4 h-4" strokeWidth={1.75} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <Label className="text-xs text-slate-500 uppercase tracking-widest">Filtrer</Label>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 h-9" data-testid="prospects-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="nouveau">Nouveau</SelectItem>
            <SelectItem value="contacte">Contacté</SelectItem>
            <SelectItem value="qualifie">Qualifié</SelectItem>
            <SelectItem value="converti">Converti</SelectItem>
            <SelectItem value="rejete">Rejeté</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <CardContent className="p-0">
          {loading ? (
            <div className="h-64 grid place-items-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : prospects.length === 0 ? (
            <div className="h-64 grid place-items-center text-slate-400 text-sm">Aucun prospect</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/50">
                  <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
                    <th className="py-3 px-4 font-medium">Entreprise</th>
                    <th className="py-3 px-4 font-medium">Contact</th>
                    <th className="py-3 px-4 font-medium">Secteur</th>
                    <th className="py-3 px-4 font-medium">Statut</th>
                    <th className="py-3 px-4 font-medium">Reçu</th>
                    <th className="py-3 px-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {prospects.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`prospect-row-${p.id}`}>
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-900 dark:text-slate-100">{p.entreprise}</div>
                        <div className="text-xs text-slate-500">{p.taille || "—"}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-slate-800 dark:text-slate-200">{p.contact_nom}</div>
                        <div className="text-xs text-slate-500 truncate max-w-[220px]">{p.email}</div>
                      </td>
                      <td className="py-3 px-4 text-slate-600 dark:text-slate-300">{p.secteur || "—"}</td>
                      <td className="py-3 px-4">
                        <Badge className={STATUT_COLOR[p.statut] || ""}>{STATUT_LABEL[p.statut] || p.statut}</Badge>
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-500 font-mono whitespace-nowrap">
                        {new Date(p.created_at).toLocaleDateString("fr-FR")}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetails(p.id)}
                          data-testid={`prospect-open-${p.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" /> Voir
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prospect-detail-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 font-display text-2xl">
                  <Building2 className="w-6 h-6 text-blue-800" />
                  {selected.entreprise}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5 mt-2">
                <div className="flex items-center gap-2">
                  <Badge className={STATUT_COLOR[selected.statut] || ""}>{STATUT_LABEL[selected.statut]}</Badge>
                  <span className="text-xs text-slate-500">
                    Reçu le {new Date(selected.created_at).toLocaleString("fr-FR")}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
                  <InfoLine icon={Mail} label="Email" value={selected.email} />
                  <InfoLine icon={Phone} label="Téléphone" value={selected.telephone} />
                  <InfoLine label="Contact" value={selected.contact_nom} />
                  <InfoLine label="Secteur" value={selected.secteur || "—"} />
                  <InfoLine label="Taille" value={selected.taille || "—"} />
                  <InfoLine label="Pays" value={selected.pays || "—"} />
                  <InfoLine label="Source" value={selected.source || "—"} />
                </div>

                {selected.fonctionnalites?.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-2 inline-flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3" /> Fonctionnalités souhaitées
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selected.fonctionnalites.map((f) => (
                        <Badge key={f} variant="outline" className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900 text-blue-800 dark:text-blue-300">
                          {FEAT_LABEL[f] || f}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selected.message && (
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-2">Message</div>
                    <div className="text-sm text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 whitespace-pre-wrap">
                      {selected.message}
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="notes" className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Notes internes admin</Label>
                  <Textarea
                    id="notes"
                    defaultValue={selected.notes_admin || ""}
                    onBlur={(e) => e.target.value !== (selected.notes_admin || "") && saveNotes(selected.id, e.target.value)}
                    placeholder="Ajouter des notes de suivi (visibles uniquement des admins)…"
                    rows={3}
                    className="mt-1.5"
                    data-testid="prospect-notes"
                  />
                </div>

                <div>
                  <Label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Changer le statut</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {["nouveau", "contacte", "qualifie"].map((s) => (
                      <Button
                        key={s}
                        variant={selected.statut === s ? "default" : "outline"}
                        size="sm"
                        onClick={() => updateStatut(selected.id, s)}
                        disabled={selected.statut === s || selected.statut === "converti" || selected.statut === "rejete"}
                        data-testid={`prospect-status-${s}`}
                      >
                        {STATUT_LABEL[s]}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-between border-t border-slate-100 dark:border-slate-800 pt-4 mt-4">
                <Button
                  variant="ghost"
                  onClick={() => remove(selected.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="prospect-delete"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                </Button>
                <div className="flex flex-col sm:flex-row gap-2">
                  {selected.statut !== "rejete" && selected.statut !== "converti" && (
                    <Button
                      variant="outline"
                      onClick={() => reject(selected.id)}
                      className="border-red-200 text-red-700 hover:bg-red-50"
                      data-testid="prospect-reject"
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Rejeter
                    </Button>
                  )}
                  {selected.statut !== "converti" && selected.statut !== "rejete" && (
                    <Button
                      onClick={() => approve(selected.id)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="prospect-approve"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approuver & créer le compte
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve result modal */}
      <Dialog open={!!approveResult} onOpenChange={(v) => !v && setApproveResult(null)}>
        <DialogContent className="max-w-lg" data-testid="prospect-approved-modal">
          {approveResult && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-display text-2xl text-emerald-700">
                  <CheckCircle2 className="w-6 h-6" /> Compte créé !
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Le compte admin a été créé pour <b>{approveResult.email}</b>. Transmettez le code ci-dessous à votre nouveau client via un canal sécurisé — <b>il ne sera plus jamais affiché</b>.
                </p>
                <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 dark:text-emerald-400 mb-2">Code d&apos;accès</div>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={approveResult.access_code} className="font-mono text-lg tracking-widest bg-white dark:bg-slate-900" data-testid="approved-access-code" />
                    <Button onClick={copyCode} variant="outline" data-testid="copy-approved-code">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => setApproveResult(null)} className="bg-blue-900 hover:bg-blue-800 text-white" data-testid="close-approved">
                  J&apos;ai copié le code
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoLine({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-1 inline-flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="text-sm text-slate-800 dark:text-slate-200 break-words">{value}</div>
    </div>
  );
}
